/**
 * Capability sandbox — Swagger-style "try it out".
 *
 * Org is locked to the reserved internal sandbox org seeded by the backend at
 * startup; operators cannot exercise capabilities against a real client tenant
 * from here. Pickers: agent → capability (filtered by agent.capabilities).
 * Input has two modes: a schema-driven form sourced from the FieldSpec[] tree
 * the backend extracts from the capability's Pydantic input model, and a raw
 * JSON editor where each item is one task's input. Submit fans out one task per
 * input; the response stacks one live-polled result panel per spawned task.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';

import { ApiError } from '@optum/api-client/client';
import { listAgents } from '@optum/api-client/endpoints/agents';
import { getCapabilitySchema, listCapabilities } from '@optum/api-client/endpoints/capabilities';
import { getCapabilityGlobalConfig } from '@optum/api-client/endpoints/capability-global-configs';
import { getOrgCapabilityConfig } from '@optum/api-client/endpoints/org-capability-configs';
import { listOrganizations } from '@optum/api-client/endpoints/organizations';
import { submitTask } from '@optum/api-client/endpoints/tasks';
import type { FieldScopes, FieldSpec, Task } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { SectionCard, SectionCardBody } from '../../ui/SectionCard.js';
import { EmptyBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { resolveInitConfig, type ConfigLayer } from '../../lib/resolve-init-config.js';

import {
  JsonInputsEditor,
  newJsonItem,
  parseJsonItems,
  type JsonItem,
} from './JsonInputsEditor.js';
import {
  SchemaForm,
  defaultsForFields,
  hasResettableOptional,
  withOptionalDefaults,
} from './SchemaForm.js';
import { Combobox } from '../../ui/Combobox.js';
import { FormShape } from '../../ui/shapes.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { Toggle } from '../../ui/Toggle.js';
import { Icon, IconButton, SplitLayout, Typography } from '../../ui/finixt.js';
import { TaskResult } from './TaskResult.js';

const INTERNAL_SANDBOX_ORG_NAME = '__internal_sandbox__';

/** Router state the sandbox reads once on mount; the sender imports this. */
export interface ReplaySeed {
  taskId: string;
  capabilityId: string;
  input: unknown;
}

/**
 * Load a failed task's capability and input, handed over by the triage screen.
 *
 * Applied once: re-running it on every render would fight the operator as soon
 * as they changed a field. The agent is chosen from the sandbox org — a task's
 * own agent belongs to a client tenant and must not be used here.
 */
function useReplaySeed({
  agents,
  apply,
  toast,
}: {
  agents: { id: string; name: string; capabilities: string[] }[] | undefined;
  apply: (seed: { capabilityId: string; agentId: string; input: unknown }) => void;
  toast: ReturnType<typeof useToast>;
}) {
  const location = useLocation();
  const seed = (location.state as { replay?: ReplaySeed } | null)?.replay ?? null;
  const applied = useRef(false);

  useEffect(() => {
    if (!seed || applied.current || !agents || agents.length === 0) return;
    const match = agents.find((a) => a.capabilities.includes(seed.capabilityId)) ?? agents[0];
    if (!match) return;
    applied.current = true;
    apply({ capabilityId: seed.capabilityId, agentId: match.id, input: seed.input });
    if (!match.capabilities.includes(seed.capabilityId)) {
      toast.info(
        `Loaded input from ${seed.taskId}`,
        `No sandbox agent carries ${seed.capabilityId} — pick one that does before submitting.`,
      );
    } else if (seed.input === null) {
      toast.info(
        `Loaded capability from ${seed.taskId}`,
        'The input was too large to inline — copy it from the task’s Input section.',
      );
    } else {
      toast.info(
        `Loaded input from ${seed.taskId}`,
        'Submitting runs the same capability against the sandbox organization.',
      );
    }
  }, [seed, agents, apply, toast]);
}
// Trusted end-user identity injected by the platform at task time; the worker
// discards any caller-supplied copy, so offering inputs here would be dead
// controls.
const PLATFORM_INJECTED_FIELDS = new Set(['user_idp', 'user_sub']);

type InputMode = 'form' | 'json';

export function SandboxPage() {
  const [agentId, setAgentId] = useState('');
  const [capabilityId, setCapabilityId] = useState('');
  const [mode, setMode] = useState<InputMode>('form');
  const [inputValue, setInputValue] = useState<Record<string, unknown>>({});
  const [jsonItems, setJsonItems] = useState<JsonItem[]>(() => [newJsonItem('')]);
  const [activeTaskIds, setActiveTaskIds] = useState<string[]>([]);
  // Default on: a sandbox run exists to be looked at, and the overhead is
  // only ever paid by an operator who asked for the run.
  const [profiling, setProfiling] = useState(true);

  const qc = useQueryClient();
  const toast = useToast();

  // ── Pickers ──────────────────────────────────────────────────────────
  const orgs = useQuery({ queryKey: qk.orgs(), queryFn: () => listOrganizations() });
  const internalOrg = useMemo(
    () => (orgs.data ?? []).find((o) => o.name === INTERNAL_SANDBOX_ORG_NAME),
    [orgs.data],
  );
  const orgId = internalOrg?.id ?? '';
  const agents = useQuery({
    queryKey: qk.agents(orgId),
    queryFn: () => listAgents({ organization_id: orgId }),
    enabled: Boolean(orgId),
  });
  const caps = useQuery({ queryKey: qk.capabilities(), queryFn: listCapabilities });

  const agent = agents.data?.find((a) => a.id === agentId);
  const allowedCapabilityIds = useMemo(() => new Set(agent?.capabilities ?? []), [agent]);
  const availableCaps = (caps.data ?? []).filter((c) => allowedCapabilityIds.has(c.id));

  useEffect(() => {
    setAgentId('');
    setCapabilityId('');
  }, [orgId]);

  // Picking a different agent invalidates the capability — except when a
  // replay sets both together, where this reset would undo the seed one tick
  // after it lands.
  const pendingCapability = useRef<string | null>(null);
  useEffect(() => {
    if (pendingCapability.current) {
      setCapabilityId(pendingCapability.current);
      pendingCapability.current = null;
      return;
    }
    setCapabilityId('');
  }, [agentId]);

  // The replayed input lands before the capability's schema does, and the
  // first schema resolution must not clobber it. Held here until that effect
  // consumes it; later capability changes reset to defaults as before.
  const seededInput = useRef<Record<string, unknown> | null>(null);
  useReplaySeed({
    agents: agents.data,
    apply: ({ capabilityId: cap, agentId: id, input }) => {
      pendingCapability.current = cap;
      setAgentId(id);
      setMode('form');
      if (input && typeof input === 'object' && !Array.isArray(input)) {
        seededInput.current = input as Record<string, unknown>;
        setInputValue(seededInput.current);
      }
    },
    toast,
  });

  // ── Schema for the chosen capability ─────────────────────────────────
  const schema = useQuery({
    queryKey: qk.capabilitySchema(capabilityId),
    queryFn: () => getCapabilitySchema(capabilityId),
    enabled: Boolean(capabilityId),
  });

  // Per-(org, capability) init-attr config, surfaced read-only so operators
  // can confirm what the capability will resolve at task time before submitting.
  const orgCapConfig = useQuery({
    queryKey: qk.orgCapConfig(orgId, capabilityId),
    queryFn: () => getOrgCapabilityConfig(orgId, capabilityId),
    enabled: Boolean(orgId) && Boolean(capabilityId),
  });
  // Layer 2 (global default) — merged under the per-org layer so the panel
  // shows the effective config that runs, not just this org's overrides.
  const globalCapConfig = useQuery({
    queryKey: qk.capabilityGlobalConfig(capabilityId),
    queryFn: () => getCapabilityGlobalConfig(capabilityId),
    enabled: Boolean(capabilityId),
    retry: (n, e) => (e instanceof ApiError && e.status === 404 ? false : n < 2),
  });

  const inputFields = useMemo(
    () => (schema.data?.input_fields ?? []).filter((f) => !PLATFORM_INJECTED_FIELDS.has(f.name)),
    [schema.data],
  );

  // Seed both editors from required-field defaults: the structured form holds
  // the object directly, the JSON editor starts from a single pretty-printed
  // template item. Re-seeds on capability change, matching the form's reset.
  // A pending replay seed wins over the defaults exactly once.
  useEffect(() => {
    if (!schema.data) return;
    const value = seededInput.current ?? defaultsForFields(inputFields);
    seededInput.current = null;
    setInputValue(value);
    setJsonItems([newJsonItem(JSON.stringify(value, null, 2))]);
  }, [schema.data, inputFields]);

  const jsonParse = useMemo(() => parseJsonItems(jsonItems), [jsonItems]);

  // ── Submit ───────────────────────────────────────────────────────────
  // Fan out one task per input. allSettled never rejects, so partial failures
  // surface here rather than in onError — succeeded tasks still render.
  const submit = useMutation({
    mutationFn: (inputs: Record<string, unknown>[]) =>
      Promise.allSettled(
        inputs.map((input) =>
          submitTask({
            agent_id: agentId,
            capability_id: capabilityId,
            input,
            enable_profiling: profiling,
          }),
        ),
      ),
    onSuccess: (results) => {
      const ok = results
        .filter((r): r is PromiseFulfilledResult<Task> => r.status === 'fulfilled')
        .map((r) => r.value);
      ok.forEach((t) => qc.setQueryData(qk.task(t.id), t));
      setActiveTaskIds(ok.map((t) => t.id));
      const failed = results.length - ok.length;
      if (failed === 0) {
        toast.success('Tasks submitted', `${ok.length} task${ok.length === 1 ? '' : 's'}`);
      } else {
        toast.error('Partial submit', `${ok.length}/${results.length} task(s) succeeded`);
      }
    },
    onError: (e) => toast.error('Submit failed', e instanceof ApiError ? e.message : String(e)),
  });

  function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const inputs = mode === 'form' ? [inputValue] : jsonParse.inputs;
    if (inputs.length === 0) return;
    submit.mutate(inputs);
  }

  const canSubmit =
    Boolean(orgId) &&
    Boolean(agentId) &&
    Boolean(capabilityId) &&
    !submit.isPending &&
    (mode === 'form' || jsonParse.allValid);

  return (
    <FormShape
      title="Sandbox"
      subtitle="Run a capability by hand against the reserved internal organization — never a client tenant."
      wide
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 64 }}>
        {/* The design's split: the request is the work, the panels beside it are
          what the run will resolve to and what came back. The seam moves
          because a profile wants width the request body does not. */}
        <SplitLayout
          meta={
            <form
              onSubmit={handleSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              <SectionCard title="Target">
                <SectionCardBody style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <PickerRow label="Organization" htmlFor="sb-org">
                    {orgs.isPending ? (
                      <LoadingBlock />
                    ) : internalOrg ? (
                      <div
                        id="sb-org"
                        aria-label="Organization"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 7,
                          borderRadius: 'var(--radius-input)',
                          border: '1px solid var(--border-hairline)',
                          background: 'var(--surface-fill)',
                          paddingLeft: 11,
                          paddingRight: 11,
                          paddingTop: 7,
                          paddingBottom: 7,
                          fontSize: 'var(--text-body-md)',
                        }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          className="mono"
                        >
                          {internalOrg.name}
                        </span>
                        <span
                          style={{
                            display: 'flex',
                            flexShrink: 0,
                            alignItems: 'center',
                            gap: 5,
                            fontSize: 'var(--text-meta-lg)',
                            color: 'var(--text-muted)',
                          }}
                        >
                          <Icon name="lock" size={13} />
                          locked
                        </span>
                      </div>
                    ) : (
                      <div
                        role="alert"
                        style={{
                          borderRadius: 'var(--radius-input)',
                          border: '1px solid var(--warn)',
                          background: 'var(--warn-tint)',
                          padding: 11,
                          fontSize: 'var(--text-meta-lg)',
                        }}
                      >
                        Internal sandbox organization not found. Restart the backend so the
                        bootstrap routine can seed it.
                      </div>
                    )}
                  </PickerRow>

                  <PickerRow label="Agent" htmlFor="sb-agent">
                    <Combobox
                      aria-label="Agent"
                      clearable
                      value={agentId}
                      onChange={setAgentId}
                      disabled={!orgId || agents.isPending}
                      placeholder={orgId ? 'Pick an agent' : 'Waiting for the sandbox org'}
                      searchPlaceholder="Search agents…"
                      options={(agents.data ?? []).map((a) => ({
                        value: a.id,
                        label: a.name,
                        meta: `${a.capabilities.length} capabilities`,
                      }))}
                    />
                  </PickerRow>

                  <PickerRow label="Capability" htmlFor="sb-cap">
                    <Combobox
                      aria-label="Capability"
                      clearable
                      value={capabilityId}
                      onChange={setCapabilityId}
                      disabled={!agentId || availableCaps.length === 0}
                      placeholder={
                        !agentId
                          ? 'Select an agent first'
                          : availableCaps.length === 0
                            ? 'No capabilities attached to this agent'
                            : 'Pick a capability'
                      }
                      searchPlaceholder="Search capabilities…"
                      options={availableCaps.map((c) => ({
                        value: c.id,
                        label: c.name,
                        meta: c.version,
                      }))}
                    />
                  </PickerRow>
                </SectionCardBody>
              </SectionCard>

              {capabilityId && (
                <SectionCard
                  title="Request body"
                  actions={
                    <>
                      {/* Form mode only: JSON mode holds raw text the operator
                      owns, and the two are deliberately not synced. Hidden
                      outright when nothing in the schema declares an optional
                      default, so it never offers a no-op. */}
                      {mode === 'form' && hasResettableOptional(inputFields) && (
                        <IconButton
                          size={30}
                          icon="restart_alt"
                          onClick={() =>
                            setInputValue(withOptionalDefaults(inputFields, inputValue))
                          }
                          title="Reset every optional field to its declared default"
                          aria-label="Reset every optional field to its declared default"
                          tone="outline"
                        />
                      )}
                      <SegmentedControl
                        items={[
                          { value: 'form', label: 'Form', icon: 'list_alt' },
                          { value: 'json', label: 'JSON', icon: 'data_object' },
                        ]}
                        value={mode}
                        onChange={(v) => setMode(v as InputMode)}
                      />
                    </>
                  }
                  {...(schema.data?.description ? { subtitle: schema.data.description } : {})}
                >
                  <SectionCardBody style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {schema.isPending && (
                      <div
                        style={{
                          borderRadius: 'var(--radius-chip)',
                          border: '1px solid var(--border-hairline)',
                          borderStyle: 'dashed',
                          padding: 11,
                          fontSize: 'var(--text-meta-lg)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        Loading schema…
                      </div>
                    )}
                    {schema.isError && (
                      <div
                        role="alert"
                        style={{
                          borderRadius: 'var(--radius-chip)',
                          border: '1px solid var(--warn)',
                          background: 'var(--surface-fill)',
                          padding: 11,
                          fontSize: 'var(--text-meta-lg)',
                        }}
                      >
                        No schema available for this capability
                        {schema.error instanceof ApiError && schema.error.status === 404
                          ? ' (backend has no loader for it yet — try Plugins → Roll now).'
                          : '.'}
                        {mode === 'json' && ' Raw JSON input still works.'}
                      </div>
                    )}

                    {mode === 'form' ? (
                      schema.data && (
                        <SchemaForm
                          fields={inputFields}
                          value={inputValue}
                          onChange={setInputValue}
                        />
                      )
                    ) : (
                      <>
                        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
                          Each input spawns one task. Use{' '}
                          <span style={{ fontWeight: 'var(--weight-medium)' }}>+ JSON</span> to
                          queue more.
                        </p>
                        <JsonInputsEditor
                          items={jsonItems}
                          errors={jsonParse.errors}
                          onChange={setJsonItems}
                        />
                      </>
                    )}
                  </SectionCardBody>

                  {/* A footer bar rather than a loose button: the card states what
                  it is about to do, and the action sits where the card ends. */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 11,
                      borderTop: '1px solid var(--border-hairline)',
                      borderColor: 'var(--border-hairline)',
                      background: 'var(--surface-sunken)',
                      paddingLeft: 16,
                      paddingRight: 16,
                      paddingTop: 11,
                      paddingBottom: 11,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
                        {canSubmit ? 'Ready to submit' : 'Fill in the required fields'}
                      </span>
                      {/* A run option, decided at submission — so it belongs with
                      the action, not in the request body above. */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <Toggle checked={profiling} onChange={setProfiling} label="Profiler" />
                        {mode === 'json' && jsonItems.length > 1 ? (
                          <Typography variant="meta">
                            Applies to all {jsonItems.length} tasks
                          </Typography>
                        ) : null}
                      </div>
                    </div>
                    <Button variant="accent" type="submit" disabled={!canSubmit}>
                      {submit.isPending
                        ? 'Submitting…'
                        : mode === 'json' && jsonItems.length > 1
                          ? `Submit ${jsonItems.length} tasks`
                          : 'Submit task'}
                    </Button>
                  </div>
                </SectionCard>
              )}
            </form>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {capabilityId && schema.data && (
              <SectionCard title="Init attributes" subtitle="What this run resolves to.">
                <SectionCardBody>
                  <InitAttrPanel
                    fields={schema.data.init_fields}
                    orgConfig={orgCapConfig.data?.config ?? {}}
                    globalConfig={globalCapConfig.data?.config ?? {}}
                    fieldScopes={globalCapConfig.data?.field_scopes ?? {}}
                    loading={orgCapConfig.isPending || globalCapConfig.isPending}
                  />
                </SectionCardBody>
              </SectionCard>
            )}

            <SectionCard
              title="Submitted"
              badge={{
                label: `${activeTaskIds.length} task${activeTaskIds.length === 1 ? '' : 's'}`,
              }}
            >
              <SectionCardBody>
                {activeTaskIds.length === 0 ? (
                  <EmptyBlock>Nothing submitted yet.</EmptyBlock>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {activeTaskIds.map((id, i) => {
                      const multi = activeTaskIds.length > 1;
                      return (
                        <div
                          key={id}
                          style={
                            multi
                              ? {
                                  borderRadius: 'var(--radius-chip)',
                                  border: '1px solid var(--border-hairline)',
                                  padding: 11,
                                }
                              : undefined
                          }
                        >
                          <TaskResult taskId={id} {...(multi ? { title: `Task #${i + 1}` } : {})} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </SectionCardBody>
            </SectionCard>
          </div>
        </SplitLayout>
      </div>
    </FormShape>
  );
}

const FRAMEWORK_FIELDS = new Set(['agent_id', 'capability_id']);

/**
 * What this capability will actually initialise with, for the sandbox org.
 *
 * Shares `resolveInitConfig` with the Resolved tab. The local version this
 * replaced took a stored org value whenever one existed, ignoring
 * `field_scopes` — so it showed an override winning that the backend discards,
 * which is the one thing a pre-flight panel must not do. There is no user
 * layer here: the operator submits as themselves and the sandbox org has no
 * per-user rows.
 */
function InitAttrPanel({
  fields,
  orgConfig,
  globalConfig,
  fieldScopes,
  loading,
}: {
  fields: FieldSpec[];
  orgConfig: Record<string, unknown>;
  globalConfig: Record<string, unknown>;
  fieldScopes: FieldScopes;
  loading: boolean;
}) {
  const visible = fields.filter((f) => !FRAMEWORK_FIELDS.has(f.name));
  if (visible.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {loading ? (
        <LoadingBlock />
      ) : (
        <dl
          style={{
            display: 'grid',
            maxHeight: '60vh',
            gridTemplateColumns: 'auto minmax(0,1fr)',
            columnGap: 16,
            rowGap: 5,
            overflowY: 'auto',
            borderRadius: 'var(--radius-chip)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-fill)',
            padding: 11,
            fontSize: 'var(--text-meta-lg)',
          }}
        >
          {resolveInitConfig({
            fields: visible,
            fieldScopes,
            global: globalConfig,
            organization: orgConfig,
            user: {},
          }).map((row) => (
            <InitAttrRow key={row.name} spec={row.spec} value={row.value} source={row.source} />
          ))}
        </dl>
      )}
      <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
        Precedence: per-org override → global default → capability default. An org value for a field
        locked to global is ignored here, exactly as it is at task time.
      </p>
    </div>
  );
}

function InitAttrRow({
  spec,
  value,
  source,
}: {
  spec: FieldSpec;
  value: unknown;
  source: ConfigLayer | 'unset';
}) {
  const isSecretRef =
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    '$secret' in (value as Record<string, unknown>);
  const display: string = (() => {
    if (isSecretRef) {
      const key = (value as Record<string, unknown>)['$secret'];
      return `$secret(${String(key ?? '')})`;
    }
    if (value === undefined) return '—';
    if (value === null) return 'null';
    if (typeof value === 'string') return value;
    return JSON.stringify(value);
  })();
  return (
    <>
      <dt style={{ color: 'var(--text-muted)' }} className="mono">
        {spec.name}
        <span style={{ marginLeft: 7, fontSize: 'var(--text-meta-lg)' }}>{spec.type}</span>
      </dt>
      <dd className="mono" style={{ color: isSecretRef ? 'var(--text-accent)' : undefined }}>
        {display}
        {source !== 'unset' && (
          <span
            style={{
              marginLeft: 7,
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--text-muted)',
            }}
          >
            ({source})
          </span>
        )}
      </dd>
    </>
  );
}

function PickerRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label
        htmlFor={htmlFor}
        style={{
          fontSize: 'var(--text-meta-lg)',
          fontWeight: 'var(--weight-medium)',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
